// Espace membres — 100% Railway backend (Mongo + Stripe + Bunny + Gelato).
// PLUS aucun lien avec WordPress / Tutor LMS / WooCommerce :
//  • Commandes : /api/portal/print-orders (Mongo PRINT_ORDERS)
//  • Apprentissage : /api/academy/series (Mongo academy_series + Bunny.net)
//  • Reco produits : /api/shop/products (Mongo gelato_products)
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, LogOut, GraduationCap, Sparkles, User, Package, Film, Clock } from "lucide-react";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { useT, useLang } from "@/lib/i18n";
import { useCurrency } from "@/context/CurrencyContext";
import { apiFetch } from "@/lib/api";

const AUTH_KEY = "geovinum.auth.v1";

function authHeaders() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    const tk = raw ? JSON.parse(raw)?.token : null;
    return tk ? { Authorization: `Bearer ${tk}` } : {};
  } catch { return {}; }
}

const fmtDuration = (sec) => {
  if (!sec) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h ? `${h}h ${m}min` : `${m} min`;
};

const DashboardPage = () => {
  const t = useT();
  const lang = useLang();
  const { formatFromEUR } = useCurrency();
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [series, setSeries] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(true);
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    if (ready && !user) navigate("/login", { replace: true });
  }, [ready, user, navigate]);

  // Commandes posters (Gelato via Stripe Checkout, stockées dans Mongo)
  useEffect(() => {
    if (!user?.email) { setOrdersLoading(false); return; }
    let cancelled = false;
    (async () => {
      const { ok, data } = await apiFetch(`/api/portal/print-orders`, { headers: authHeaders() });
      if (!cancelled) {
        setOrders(ok ? (data?.items || []) : []);
        setOrdersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.email]);

  // Académie : séries publiées (depuis Mongo `academy_series` + métadonnées Bunny.net)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await apiFetch(`/api/academy/series?per_page=6`);
      if (!cancelled) {
        setSeries(ok ? (data?.items || []) : []);
        setSeriesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Recommandations produits (boutique Gelato)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await apiFetch(`/api/shop/products?limit=4&sort=popular`);
      if (!cancelled) setRecommendations(ok ? (data?.items || []) : []);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!ready || !user) return null;

  const dateLocale = { en: "en-GB", fr: "fr-FR", es: "es-ES", pt: "pt-PT" }[lang] || "en-GB";

  return (
    <Layout testId="page-dashboard">
      <section className="pt-12 md:pt-16 pb-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-[32px] md:text-[44px] leading-[1.1] text-[color:var(--gv-ink)]">
              {t("dashboard.welcome_back")} <span aria-hidden>🍷</span>
            </h1>
            <div className="text-[14px] text-[color:var(--gv-ink)]/60 mt-1">{t("dashboard.signed_in_as")} {user.email}</div>
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-ghost" data-testid="dashboard-avatar">
              <User size={14} /> {user.name}
            </button>
            <button onClick={() => { logout(); navigate("/"); }} className="btn-ghost" data-testid="dashboard-logout">
              <LogOut size={14} /> {t("dashboard.logout")}
            </button>
          </div>
        </div>
      </section>

      {/* Commandes (Railway / Mongo PRINT_ORDERS) */}
      <Section title={t("dashboard.your_maps")} desc={t("dashboard.your_maps_desc")} testId="dashboard-maps">
        {ordersLoading ? (
          <Loading />
        ) : orders.length === 0 ? (
          <EmptyState
            title={t("dashboard.empty_maps")}
            cta={<Link to="/shop" className="btn-primary">{t("cta.explore_maps")} <ArrowRight size={14} /></Link>}
            testId="empty-maps"
          />
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.session_id} className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid={`order-${o.session_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-[5px] bg-[color:var(--gv-mist)] flex items-center justify-center">
                      <Package size={16} className="text-[color:var(--gv-blue)]" />
                    </div>
                    <div>
                      <div className="font-display text-[16px] text-[color:var(--gv-ink)]">#{o.session_id?.slice(-8)?.toUpperCase()}</div>
                      <div className="text-[12px] text-[color:var(--gv-ink)]/60">
                        {o.created_at ? new Date(o.created_at).toLocaleDateString(dateLocale, { day: "numeric", month: "short", year: "numeric" }) : ""}
                        {" · "}<span className="capitalize">{(o.status || "received").replace(/_/g, " ")}</span>
                      </div>
                    </div>
                  </div>
                  <div className="font-display text-[18px] text-[color:var(--gv-ink)]">
                    {formatFromEUR(o.amount_cents || 0)}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-[13px] text-[color:var(--gv-ink)]/75">
                  <span className="capitalize">{(o.slug || "").replace(/-/g, " ")} · {o.size_id}</span>
                  {o.tracking_url && (
                    <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-[color:var(--gv-pink)] hover:underline" data-testid={`order-track-${o.session_id}`}>
                      {t("dashboard.track_shipment")} <ArrowRight size={11} className="inline" />
                    </a>
                  )}
                </div>
              </div>
            ))}
            <Link to="/portal/orders" className="inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-pink)] hover:underline" data-testid="dashboard-orders-all">
              {t("dashboard.view_all_orders")} <ArrowRight size={12} />
            </Link>
          </div>
        )}
      </Section>

      {/* Séries Academy (Railway / Mongo academy_series + Bunny.net) */}
      <Section title={t("dashboard.continue_learning")} desc={t("dashboard.continue_learning_desc")} testId="dashboard-learning">
        {seriesLoading ? (
          <Loading />
        ) : series.length === 0 ? (
          <EmptyState
            title={t("dashboard.empty_learning")}
            cta={<Link to="/academy" className="btn-primary">{t("cta.start_learning")} <ArrowRight size={14} /></Link>}
            testId="empty-learning"
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {series.map((s) => (
              <Link
                key={s.slug}
                to={`/academy/series/${s.slug}`}
                className="group block bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden hover:border-[color:var(--gv-pink)] transition-colors"
                data-testid={`series-${s.slug}`}
              >
                <div className="aspect-[16/9] bg-[color:var(--gv-mist)] overflow-hidden">
                  {s.hero_image_url
                    ? <img src={s.hero_image_url} alt={s.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
                    : <div className="w-full h-full flex items-center justify-center"><GraduationCap size={32} className="text-[color:var(--gv-blue)]/40" /></div>}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--gv-blue)] mb-1.5">
                    <GraduationCap size={12} /> Academy
                  </div>
                  <div className="font-display text-[17px] text-[color:var(--gv-ink)] leading-snug mb-1.5 line-clamp-2 group-hover:text-[color:var(--gv-pink)] transition-colors">{s.title}</div>
                  <div className="text-[11.5px] text-[color:var(--gv-ink)]/55 flex items-center gap-2">
                    <Film size={11} /> {s.videos_count}
                    <Clock size={11} className="ml-1" /> {fmtDuration(s.total_length_sec)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>

      {/* Recommandations produits (Railway / Mongo gelato_products) */}
      {recommendations.length > 0 && (
        <Section title={t("dashboard.recommendations")} desc={t("dashboard.recommendations_desc")} testId="dashboard-recs" bg>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {recommendations.map((p) => (
              <Link
                key={p.product_id}
                to={`/shop/${p.product_id}`}
                className="block bg-white rounded-[5px] overflow-hidden border border-[color:var(--gv-line)] hover:border-[color:var(--gv-pink)] transition-colors"
                data-testid={`reco-${p.product_id}`}
              >
                <div className="aspect-[3/4] overflow-hidden bg-[color:var(--gv-mist)]">
                  {p.preview && <img src={p.preview} alt={p.title} className="w-full h-full object-cover" loading="lazy" />}
                </div>
                <div className="p-4">
                  <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/50 mb-1.5">
                    {formatFromEUR(p.from_amount_cents || 0)}
                  </div>
                  <div className="font-display text-[15px] text-[color:var(--gv-ink)] line-clamp-2">{p.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <h3 className="font-display text-[28px] md:text-[40px] text-[color:var(--gv-ink)] mb-6">{t("dashboard.explore_more")} <span aria-hidden>🍷</span></h3>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/shop" className="btn-primary">{t("cta.explore_maps")} <ArrowRight size={16} /></Link>
            <Link to="/academy" className="btn-secondary">{t("cta.start_learning")} <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

const Section = ({ title, desc, testId, bg, children }) => (
  <section className={`py-12 md:py-14 ${bg ? "bg-[color:var(--gv-mist)]" : ""}`} data-testid={testId}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="mb-7">
        <h2 className="font-display text-[24px] md:text-[32px] text-[color:var(--gv-ink)]">{title}</h2>
        <p className="text-[13.5px] text-[color:var(--gv-ink)]/60 mt-1">{desc}</p>
      </div>
      {children}
    </div>
  </section>
);

const EmptyState = ({ title, cta, testId }) => (
  <div data-testid={testId} className="border border-dashed border-[color:var(--gv-line)] rounded-[6px] p-10 text-center">
    <Sparkles size={24} className="mx-auto text-[color:var(--gv-blue)]/50 mb-3" />
    <div className="font-display text-[18px] text-[color:var(--gv-ink)] mb-5">{title}</div>
    {cta}
  </div>
);

const Loading = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
    {[0, 1, 2].map((i) => (
      <div key={i} className="bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden">
        <div className="aspect-[16/9] bg-[color:var(--gv-mist)] animate-pulse" />
        <div className="p-4 space-y-2">
          <div className="h-3 bg-[color:var(--gv-mist)] rounded animate-pulse w-1/3" />
          <div className="h-4 bg-[color:var(--gv-mist)] rounded animate-pulse w-4/5" />
        </div>
      </div>
    ))}
  </div>
);

export default DashboardPage;
